import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HeroBalanceCard } from '@/components/Dashboard/HeroBalanceCard';
import { StatCard } from '@/components/Dashboard/StatCard';
import { RecentExpensesList } from '@/components/Dashboard/RecentExpensesList';
import { QuickTipBanner } from '@/components/Dashboard/QuickTipBanner';
import { FloatingActionButton } from '@/components/FloatingActionButton';
import { DashboardPageSkeleton } from '@/components/Skeleton/DashboardPageSkeleton';
import { api, type ExpenseSummary, type HsaAccount, type OnboardingStatus } from '@/api/client';
import SetupChecklist from '@/components/Dashboard/SetupChecklist';
import StealthIraPrompt from '@/components/Prompts/StealthIraPrompt';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<ExpenseSummary | null>(null);
  const [hsaAccounts, setHsaAccounts] = useState<HsaAccount[]>([]);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, accountsData, statusData] = await Promise.all([
        api.getExpenseSummary(),
        api.getHsaAccounts(),
        api.getOnboardingStatus(),
      ]);
      setSummary(summaryData);
      if (accountsData) setHsaAccounts(accountsData);
      if (statusData) setOnboardingStatus(statusData);
    } catch (err: any) {
      setError(err?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="p-8 space-y-6">
        <DashboardPageSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
        <p className="text-slate-600 mb-4">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="bg-navy text-white px-5 py-2 rounded-lg font-semibold hover:opacity-90 transition"
        >
          Retry
        </button>
      </div>
    );
  }

  const handleExpenseClick = (expense: { id: string }) => {
    navigate(`/expenses?expenseId=${expense.id}`);
  };

  // Sum HSA account balances
  const totalHsaBalance = hsaAccounts.reduce((sum, acct) => sum + (acct.balance || 0), 0);

  return (
    <div className="p-8 space-y-6">
      {/* Setup Checklist (shown until dismissed or all items complete) */}
      {onboardingStatus && !onboardingStatus.onboardingDismissed && (
        <SetupChecklist
          status={onboardingStatus}
          onDismiss={() => setOnboardingStatus(s => s ? { ...s, onboardingDismissed: true } : s)}
        />
      )}

      {/* Stealth IRA prompt (engagement-based modal) */}
      {onboardingStatus?.stealthIraPromptDue && (
        <StealthIraPrompt
          status={onboardingStatus}
          onClose={() => setOnboardingStatus(s => s ? { ...s, stealthIraPromptDue: false } : s)}
        />
      )}

      {/* Hero Balance Card */}
      <HeroBalanceCard
        totalHsaBalance={totalHsaBalance}
        accountCount={hsaAccounts.length}
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          label="Total Unreimbursed"
          value={`$${(summary?.totalUnreimbursed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtext={`${summary?.unreimbursedCount || 0} expenses`}
          valueColor="#C9A227"
        />
        <StatCard
          label="Total Reimbursed"
          value={`$${(summary?.totalReimbursed || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          subtext={`${(summary?.expenseCount || 0) - (summary?.unreimbursedCount || 0)} expenses`}
        />
      </div>

      {/* Recent Expenses */}
      <RecentExpensesList onAddExpense={() => navigate('/expenses')} onExpenseClick={handleExpenseClick} />

      {/* Quick Tip */}
      <QuickTipBanner />

      {/* Floating Action Button - Navigate to Expenses page in add mode */}
      <FloatingActionButton onClick={() => navigate('/expenses')} />
    </div>
  );
}
